import { useEffect, useSyncExternalStore } from "react";
import { subscribeRealtimeEvent } from "./realtime";

/**
 * Alerts for new food orders and bookings made by someone else (a player in the app
 * or a colleague): a short chime, and a flashing tab title while the tab is hidden.
 * Actions taken on this screen are marked local and stay silent.
 */

const SOUND_KEY = "gaamed.web.provider.sound";
// The realtime event can beat the API response that tells us the action was ours.
const LOCAL_GRACE_MS = 1200;

const localActivityIds = new Set<string>();

/** Call with the id of an order or booking this screen just created. */
export function markLocalActivity(id: string | null | undefined) {
  if (!id) return;
  localActivityIds.add(id);
  setTimeout(() => localActivityIds.delete(id), 60_000);
}

const soundListeners = new Set<() => void>();

function readSoundEnabled() {
  try {
    return localStorage.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean) {
  try {
    localStorage.setItem(SOUND_KEY, enabled ? "on" : "off");
  } catch {
    // Storage can be unavailable (private mode); the choice then lasts for this page only.
  }
  cachedSoundEnabled = enabled;
  soundListeners.forEach((listener) => listener());
}

let cachedSoundEnabled: boolean | null = null;
function getSoundEnabled() {
  if (cachedSoundEnabled === null) cachedSoundEnabled = readSoundEnabled();
  return cachedSoundEnabled;
}

export function useSoundEnabled() {
  return useSyncExternalStore(
    (listener) => {
      soundListeners.add(listener);
      return () => soundListeners.delete(listener);
    },
    getSoundEnabled,
    () => true,
  );
}

let audioContext: AudioContext | null = null;

/** Two soft rising tones. Browsers allow audio once the page has had a click. */
export function playChime() {
  try {
    const AudioCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) return;
    audioContext ??= new AudioCtor();
    const context = audioContext;
    if (context.state === "suspended") void context.resume();
    [660, 880].forEach((frequency, index) => {
      const start = context.currentTime + index * 0.16;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.3);
    });
  } catch {
    // Audio is a nicety; never let it break the page.
  }
}

let titleTimer: ReturnType<typeof setInterval> | null = null;
let originalTitle: string | null = null;

function stopTitleFlash() {
  if (titleTimer) clearInterval(titleTimer);
  titleTimer = null;
  if (originalTitle !== null) document.title = originalTitle;
  originalTitle = null;
}

/** Alternate the tab title with `message` until the person comes back to the tab. */
export function flashTitle(message: string) {
  if (typeof document === "undefined" || !document.hidden) return;
  stopTitleFlash();
  originalTitle = document.title;
  let showMessage = true;
  titleTimer = setInterval(() => {
    document.title = showMessage ? `● ${message}` : originalTitle ?? "";
    showMessage = !showMessage;
  }, 1000);
  const onVisible = () => {
    if (!document.hidden) {
      stopTitleFlash();
      document.removeEventListener("visibilitychange", onVisible);
    }
  };
  document.addEventListener("visibilitychange", onVisible);
}

/** Wire alerts for the signed-in provider. `labels` are already translated. */
export function useActivityAlerts(enabled: boolean, labels: { order: string; booking: string; extension?: string }) {
  useEffect(() => {
    if (!enabled) return;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const seenExtensionRequests = new Set<string>();
    const unsubscribe = subscribeRealtimeEvent((event) => {
      let id: string | undefined;
      let message: string | undefined;
      const payload = (event.payload ?? {}) as Record<string, unknown>;
      if (event.type === "food_order.created") {
        id = typeof payload.order_id === "string" ? payload.order_id : undefined;
        message = labels.order;
      } else if (event.type === "booking.created") {
        id = typeof payload.booking_id === "string" ? payload.booking_id : undefined;
        message = labels.booking;
      } else if (event.type === "session.extension_requested.v1") {
        const quoteId = typeof payload.quote_id === "string" && payload.quote_id.length > 0 ? payload.quote_id : null;
        if (!quoteId || typeof payload.session_id !== "string" || !payload.session_id ||
            typeof payload.venue_id !== "string" || !payload.venue_id || seenExtensionRequests.has(quoteId)) return;
        if (seenExtensionRequests.size >= 256) seenExtensionRequests.delete(seenExtensionRequests.values().next().value!);
        seenExtensionRequests.add(quoteId);
        id = quoteId;
        message = labels.extension;
      }
      if (!id || !message) return;
      const activityId = id;
      const text = message;
      const timer = setTimeout(() => {
        timers.delete(timer);
        if (localActivityIds.has(activityId)) return;
        if (getSoundEnabled()) playChime();
        flashTitle(text);
      }, LOCAL_GRACE_MS);
      timers.add(timer);
    });
    return () => {
      unsubscribe();
      timers.forEach((timer) => clearTimeout(timer));
      stopTitleFlash();
    };
  }, [enabled, labels.booking, labels.order, labels.extension]);
}
